import type { ProposalDetail, ProposedAction } from '../core/types';
import { hasFailedExecutionResult } from './actionStatus';

export interface PartialExecutionState {
  /** Actions that already ran and committed successfully. Independent of is_selected. */
  alreadyExecuted: ProposedAction[];
  /** Actions whose execution resulted in an error state. */
  failed: ProposedAction[];
  /**
   * Selected current-version actions that re-approval will (re-)execute: every
   * selected action that has NOT already committed a real success AND is not
   * pruned by the executor's dependency rule. This is a superset of `failed` — it
   * also includes selected actions that were never attempted on the prior run —
   * but it EXCLUDES a selected action whose (transitive) dependency is deselected,
   * because the executor drops those too (see
   * `ExecuteActionService._compute_skips`). The banner uses this count so its copy
   * reflects exactly what re-approval will run, not just the failures and not
   * actions the executor will silently skip.
   */
  willReExecute: ProposedAction[];
}

/**
 * Action types whose handler declares `has_external_side_effects = True` on
 * the backend (see `apps/actions/services/execute_action_service.py`'s
 * `_is_already_done`). TypeScript cannot read a Python ClassVar, so this set
 * mirrors it — and `scripts/check_external_side_effect_parity.py` (a pre-commit
 * hook) fails when the two drift, so a handler added on the backend cannot
 * silently leave this list stale.
 */
const EXTERNAL_SIDE_EFFECT_ACTION_TYPES = new Set([
  'gmail_send',
  'calendar_event_create',
  'onboarding_action',
]);

/**
 * Whether `action` already recorded a real, committed success — mirrors the
 * backend's `ExecuteActionService._is_already_done`: a handler registered for
 * the action's type that declares `has_external_side_effects`,
 * `execution_result.status === 'success'`, and not a synthesised mock/demo-mode
 * envelope. A simulated send is not evidence of a real side effect, so it must
 * never be reported as "already ran". CRM/timeline actions have no external
 * side effect and are always re-dispatched by the backend, so they must never
 * be reported as already executed either — see
 * `EXTERNAL_SIDE_EFFECT_ACTION_TYPES`.
 *
 * `executed_at` is deliberately NOT part of this predicate: the backend skips a
 * successful external action on re-approval regardless of whether `executed_at`
 * is set, so gating on it here would let the UI offer a retry affordance for an
 * action the executor will silently skip. The `is_current_version` guard is a
 * UI concern (a superseded edit is never shown as already-run), not part of the
 * backend rule.
 */
export function isActionAlreadyExecuted(action: ProposedAction): boolean {
  if (
    !EXTERNAL_SIDE_EFFECT_ACTION_TYPES.has(action.proposed_action_type_name)
  ) {
    return false;
  }
  if (action.is_current_version === false) {
    return false;
  }
  if (action.execution_result?.status !== 'success') {
    return false;
  }
  const metadata = action.execution_result?.metadata;
  if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
    const record = metadata as Record<string, unknown>;
    if (record.mock || record.demo_mode) {
      return false;
    }
  }
  return true;
}

/**
 * Actions the executor will skip because a dependency won't run — mirrors the
 * backend `ExecuteActionService._compute_skips`. Seeded with the deselected
 * actions (an already-executed action keeps its recorded success and is never
 * skipped), then transitively widened: any action whose `depends_on_action_ids`
 * includes a skipped action is itself skipped, because its prerequisite won't
 * exist. Returns the set of skipped action ids.
 */
function computeDependencySkips(actions: ProposedAction[]): Set<number> {
  const alreadyExecutedIds = new Set(
    actions.filter(isActionAlreadyExecuted).map(a => a.id)
  );
  const dependsOn = new Map<number, number[]>();
  for (const action of actions) {
    dependsOn.set(action.id, action.depends_on_action_ids ?? []);
  }

  const skips = new Set<number>();
  for (const action of actions) {
    if (action.is_selected === false && !alreadyExecutedIds.has(action.id)) {
      skips.add(action.id);
    }
  }

  let changed = true;
  while (changed) {
    changed = false;
    for (const action of actions) {
      if (skips.has(action.id) || alreadyExecutedIds.has(action.id)) {
        continue;
      }
      const blocked = (dependsOn.get(action.id) ?? []).some(dep =>
        skips.has(dep)
      );
      if (blocked) {
        skips.add(action.id);
        changed = true;
      }
    }
  }
  return skips;
}

/**
 * Determine whether a pending proposal is in a partial-failure retry state,
 * and which of its current-version actions already ran vs. failed.
 *
 * Returns `null` when the state does not apply:
 *   - proposal is not pending
 *   - no current-version actions committed successfully yet
 *
 * A pending proposal with only failures (and no successes) is treated as an
 * ordinary first-attempt retry and returns `null` — no partial-execution
 * warning is needed.
 */
export function getPartialExecutionState(
  proposal: Pick<ProposalDetail, 'resolution' | 'actions'>
): PartialExecutionState | null {
  if (proposal.resolution !== 'pending') {
    return null;
  }

  const currentVersionActions = proposal.actions.filter(
    a => a.is_current_version !== false
  );

  const alreadyExecuted = currentVersionActions.filter(isActionAlreadyExecuted);

  // Only actions with a real failed execution result (`error`/`failure`/
  // `partial_success`) count toward the retry total — never transient
  // (`in_flight`) or unknown statuses that `getActionStatus` would otherwise
  // fold into `'error'`. Deselected actions are excluded: a failure verdict on
  // an action the user has since unchecked is not part of the pending retry.
  const failed = currentVersionActions.filter(
    a => a.is_selected !== false && hasFailedExecutionResult(a)
  );

  // Everything re-approval will run: every selected action that has not already
  // committed a real success and is not dependency-pruned. This includes the
  // failed actions AND selected actions that were never attempted on the prior
  // run — both execute on re-approval — but excludes a selected action whose
  // dependency is deselected, because the executor prunes those too (see
  // computeDependencySkips). Deriving the count from the same pruned set keeps
  // the banner's promise accurate.
  const dependencySkips = computeDependencySkips(currentVersionActions);
  const willReExecute = currentVersionActions.filter(
    a =>
      a.is_selected !== false &&
      !isActionAlreadyExecuted(a) &&
      !dependencySkips.has(a.id)
  );

  if (alreadyExecuted.length === 0) {
    return null;
  }

  return { alreadyExecuted, failed, willReExecute };
}
